/**
 * The theme choice while the app is open (spec docs/superpowers/specs/2026-09-27-app-ready-made-themes-design.md,
 * "Ruling 2", "Ruling 3" and "Ruling 6"). One module-level store, read with useSyncExternalStore:
 *
 *   - the app sidebar's scope enters it when an app page mounts and leaves it when the page goes;
 *     <html> carries the look exactly while at least one app page is open;
 *   - someone who never chose has no choice (null): <html> carries nothing, as before themes existed;
 *   - the Theme menu (components/app-theme/app-theme-menu.tsx) reads it, previews through it and saves
 *     through it — the menu lives in the root layout's header, outside the app page's tree, which is
 *     why this is a store and not a React context;
 *   - the account's answer comes in through adoptAccountAppTheme, which refuses an answer that a save
 *     has overtaken.
 *
 * SAVED AND PAINTED ARE TWO THINGS (spec docs/superpowers/specs/2026-10-29-branding-kit-and-brand-theme-design.md,
 * section 3.6). `saved` is the person's own choice. What the page paints comes from it and the server's answer
 * (effectiveChoice, ./brand-theme.ts): a company's brand is the default for someone who never chose, and it is never
 * saved for them. Only a person's own click saves.
 *
 * The cookie mirrors what is PAINTED, because the first paint has nothing else to go on. On arrival it is the best word
 * there is for what they chose, until the account answers; two things keep it from being taken for a choice:
 *
 *   - `brand` in the cookie is never a choice. Who chose it is the account's to say, and the page may be showing it
 *     because it is the company's default. It is `held`: painted, on the cache's variables, until the server says
 *     otherwise, or the person clicks. A failed read therefore leaves the look alone.
 *   - a cookie equal to what this tab paints is this tab's own paint, not news from another tab, whatever it says: a saved
 *     brand with no tokens mirrors `classic`, and the brand is still what the person chose.
 *
 * ONE RULE FOR THE RACE (the icon sets keep the same one, ../icon-sets.ts). The account and the server answer in either
 * order on every load, and only the server knows whether there is a brand to paint. So until its first answer an account answer
 * records `saved` and nothing else: the look the page arrived with is `held`, and neither <html> nor the cookie moves. It cannot
 * be only the brand that is held, because a saved brand with no tokens leaves `classic` in the cookie, and the account's `brand`
 * would take that off the page. The exception is a person with no workspace to read, for whom no answer will ever come
 * (`readComing` false).
 *
 * WHOSE LOOK THIS IS. A failed read leaving the look alone is right for the workspace the look belongs to, and only for that one: a
 * switch reloads nothing, so after one `brand` still says what the workspace left behind said, and for an MSSP user that is another
 * client. `lookOf` remembers whose it is. A read for any other workspace that comes to nothing (noBrandAnswerFor) takes it down:
 * no colours, the person's own look. While that read is out the page stands, and whatever read lands next paints.
 */
import { readPersistedWorkspaceId } from '@/lib/tenant/workspace-id-storage'
import { APP_BRAND_THEME, DEFAULT_APP_THEME, isAppThemeChoice, type AppThemeChoice } from './app-themes'
import { readAppThemeCookie, writeAppThemeCookie } from './app-theme-cookie'
import { applyAppTheme, clearAppTheme, isAppThemeOffPath, markAppThemePreview } from './app-theme-dom'
import {
  DEFAULT_BRAND_SIDEBAR_MARK,
  applyBrandSidebarMark,
  applyBrandTokens,
  brandTokenVars,
  cleanBrandVars,
  clearBrandTokenCache,
  clearBrandTokens,
  effectiveChoice,
  readBrandTokenCache,
  usableBrandTokens,
  writeBrandTokenCache,
  type EffectiveBrandTheme,
} from './brand-theme'

export interface AppThemeSnapshot {
  /** Is an app page (one under the app sidebar) open? The menu offers the themes only then. */
  inAppShell: boolean
  /** The look they have: their choice, or the company's brand if they never chose. Null: nothing at all. */
  theme: AppThemeChoice | null
  /** What <html> shows: a choice being previewed, else `theme`. */
  shown: AppThemeChoice | null
}

/** What the server renders with: no app page, no choice. */
export const SERVER_APP_THEME_SNAPSHOT: AppThemeSnapshot = Object.freeze({
  inAppShell: false,
  theme: null,
  shown: null,
})

let scopes = 0
let fontClass: string | undefined
/** The person's own choice, as far as this tab knows. */
let saved: AppThemeChoice | null = null
/** What the server last said for this page. Null until it has: a failed read leaves it where it was. */
let brand: EffectiveBrandTheme | null = null
/** The look the page keeps until the server's first answer, or a person's click. Null: nothing is being kept. */
let held: { look: AppThemeChoice | null } | null = null
/** The workspace the look on the page belongs to: the one `brand` answered for, or the one the page arrived in. */
let lookOf: string | null = null
/** A read for this workspace came to nothing: there is no brand here to paint, even for someone whose own choice it is. */
let unanswered = false
let preview: AppThemeChoice | null = null
/** The look a preview stands in front of — the painted look as the menu names it. */
let standing: AppThemeChoice | null = null
let snapshot: AppThemeSnapshot = SERVER_APP_THEME_SNAPSHOT
const listeners = new Set<() => void>()

let savesInFlight = 0
let epoch = 0

/** The look the page has, previews aside. */
function painted(): AppThemeChoice | null {
  if (brand !== null) return effectiveChoice(saved, brand)
  if (held) return held.look
  return unanswered && saved === APP_BRAND_THEME ? 'classic' : saved
}

function emit(brandChanged = false) {
  const theme = painted()
  const next: AppThemeSnapshot = { inAppShell: scopes > 0, theme, shown: preview ?? theme }
  const changed = next.inAppShell !== snapshot.inAppShell || next.theme !== snapshot.theme || next.shown !== snapshot.shown
  if (changed) snapshot = next
  if (changed || brandChanged) for (const listener of [...listeners]) listener()
}

const onAppPage = () => scopes > 0 && (typeof window === 'undefined' || !isAppThemeOffPath(window.location.pathname))

/** Make <html> match the store: the shown look while an app page is open on an app path, else nothing. */
function paint() {
  if (typeof document === 'undefined') return
  if (!onAppPage()) {
    clearAppTheme(fontClass)
    clearBrandTokens()
    return
  }
  const shown = preview ?? painted()
  if (shown === null) {
    clearAppTheme(fontClass)
    return
  }
  applyAppTheme(shown, fontClass)
  markAppThemePreview(preview ? standing : null)
}

/** Can `choice` be shown in front of the look? Anything but a brand with no tokens. */
function mayShow(choice: AppThemeChoice): boolean {
  return choice !== APP_BRAND_THEME || (brand !== null && usableBrandTokens(brand) !== null)
}

export function subscribeAppTheme(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const getAppThemeSnapshot = (): AppThemeSnapshot => snapshot
export const getServerAppThemeSnapshot = (): AppThemeSnapshot => SERVER_APP_THEME_SNAPSHOT

/** The person's own choice: what a failed save goes back to, never the company's default. */
export const getSavedAppTheme = (): AppThemeChoice | null => saved

/** What the server last said for this page, for the menu; null until it has answered. */
export const getEffectiveBrandSnapshot = (): EffectiveBrandTheme | null => brand

/**
 * An app page opened: read the mirror and paint it. Returns the function that closes it; the last one
 * closed takes the choice (and any preview, and the brand's variables) off <html>.
 */
export function enterAppShell(font?: string): () => void {
  scopes += 1
  if (font) fontClass = font
  if (scopes === 1) {
    const mirrored = readAppThemeCookie()
    lookOf = readPersistedWorkspaceId()
    if (mirrored?.theme === APP_BRAND_THEME) {
      // No request has answered, so the cache is all there is. No usable cache: no brand at all, the same as first paint.
      const cache = onAppPage() ? readBrandTokenCache(mirrored.owner, lookOf) : null
      held = cache !== null && applyBrandTokens(cache.vars) ? { look: APP_BRAND_THEME } : null
      if (held !== null && cache !== null) applyBrandSidebarMark(cache.mark)
    } else {
      held = null
      saved = mirrored?.theme ?? null
    }
  }
  paint()
  emit()
  let open = true
  return () => {
    if (!open) return
    open = false
    scopes = Math.max(0, scopes - 1)
    if (scopes === 0) {
      preview = null
      standing = null
      // A move to the portal, or another person signing in on this tab: the next page starts from its own answer.
      brand = null
      held = null
      lookOf = null
      unanswered = false
    }
    paint()
    emit(true)
  }
}

/** The choice is now `next`: mirror and paint what that makes the look. */
function choose(next: AppThemeChoice | null, owner: string | null | undefined) {
  saved = next
  const look = painted()
  if (preview === look) {
    preview = null
    standing = null
  } else if (preview !== null && look !== null) {
    standing = look
  }
  writeAppThemeCookie(look, owner)
  paint()
  emit()
}

/**
 * The user chose `next` (null: no choice — the page as it was before themes): keep it, mirror what is now painted
 * (`<choice>.<owner>`, or no cookie) and paint it. Their click ends a brand held on the cookie's word.
 */
export function setAppTheme(next: AppThemeChoice | null, owner: string | null | undefined): void {
  if (next !== null && !isAppThemeChoice(next)) return
  held = null
  choose(next, owner)
}

/**
 * Show a choice without saving it, in front of `standingFor` — the look the menu calls the person's own
 * (what is painted, or for someone with nothing painted, Classic or Custom as their builder has it). Null, the look
 * already there, or a brand with no tokens, ends the preview.
 */
export function previewAppTheme(next: AppThemeChoice | null, standingFor?: AppThemeChoice): void {
  const behind = standingFor ?? painted() ?? DEFAULT_APP_THEME
  preview = next !== null && isAppThemeChoice(next) && next !== behind && mayShow(next) ? next : null
  standing = preview ? behind : null
  paint()
  emit()
}

/**
 * Another tab may have saved a choice (no event crosses tabs): read the cookie again. What this tab paints is its own
 * mirror and says nothing, and `brand` is what some tab paints, never a choice.
 */
export function refreshAppThemeFromCookie(): void {
  const mirrored = readAppThemeCookie()?.theme ?? null
  if (mirrored === painted() || mirrored === APP_BRAND_THEME) return
  saved = mirrored
  held = null
  paint()
  emit()
}

// ── Saves and the account's answer ───────────────────────────────────────────────────────────
//
// GET app-theme says the account's choice. An answer that was on its way while the user saved a new
// choice may carry the OLD one, and must not overwrite the new. So a save moves an epoch when it
// starts and when it ends; an answer is adopted only when no save was in flight when it was asked and
// the epoch has not moved since.

export function beginAppThemeSave(): void {
  savesInFlight += 1
  epoch += 1
}

export function endAppThemeSave(): void {
  savesInFlight = Math.max(0, savesInFlight - 1)
  epoch += 1
}

export const appThemeSaving = (): boolean => savesInFlight > 0
export const appThemeSaveEpoch = (): number => epoch

/**
 * The account says `answer` (asked at `askedAt`; null: the person never chose). Keep it, and mirror and paint what that
 * makes the look, unless a save overtook it. With `readComing` and no answer from the server yet, it is only kept: see the
 * header. Pass `readComing` false for someone with no workspace to read.
 */
export function adoptAccountAppTheme(
  answer: AppThemeChoice | null,
  owner: string | null | undefined,
  askedAt: number,
  readComing = false,
): boolean {
  if (savesInFlight > 0 || askedAt !== epoch) return false
  if (answer !== null && !isAppThemeChoice(answer)) return false
  if (readComing && brand === null) {
    held ??= { look: painted() }
    saved = answer
    return true
  }
  held = null
  choose(answer, owner)
  return true
}

/**
 * The server says what this page paints with (GET branding/theme/effective, a successful read: a failed one never gets
 * here). The variables go on <html> whenever there are tokens, chosen or not, because a preview of the brand needs them;
 * the cache follows, replaced when the version differs or it answered for another workspace, and goes with the variables when the
 * brand is no longer effective. With no owner (a super-admin viewing as someone) the page paints and this browser keeps nothing.
 * `workspaceId` is the workspace the read went out under.
 */
export function adoptEffectiveBrandTheme(next: EffectiveBrandTheme, owner: string | null | undefined, workspaceId: string | null = readPersistedWorkspaceId()): void {
  brand = next
  lookOf = workspaceId
  unanswered = false
  const tokens = usableBrandTokens(next)
  const vars = tokens && cleanBrandVars(brandTokenVars(tokens))
  if (vars === null) {
    clearBrandTokens()
    clearBrandTokenCache()
  } else {
    const mark = next.sidebarMark ?? DEFAULT_BRAND_SIDEBAR_MARK
    if (onAppPage()) {
      applyBrandTokens(vars)
      applyBrandSidebarMark(mark)
    }
    // The version moves with the mark, so a new mark rewrites the cache with the colours.
    if (owner && readBrandTokenCache(owner, workspaceId)?.version !== next.version) writeBrandTokenCache(owner, next.version, vars, workspaceId, mark)
  }
  if (preview !== null && !mayShow(preview)) {
    preview = null
    standing = null
  }
  writeAppThemeCookie(painted(), owner)
  paint()
  emit(true)
}

/**
 * The read for `workspaceId` came to nothing (a failed read, or one that answered no answer) and no answer for it came before. If
 * the look on the page is that workspace's it is still true, and stays. If it is another workspace's it comes down: the brand and
 * its variables, and the cookie follows. The cache stays: it names the workspace it belongs to, and paints nowhere else.
 * Whichever read lands next paints as usual. Returns whether it took anything down, for the icon set to do the same.
 */
export function noBrandAnswerFor(workspaceId: string, owner: string | null | undefined): boolean {
  if (lookOf === workspaceId) return false
  brand = null
  held = null
  lookOf = workspaceId
  unanswered = true
  clearBrandTokens()
  if (preview !== null && !mayShow(preview)) {
    preview = null
    standing = null
  }
  writeAppThemeCookie(painted(), owner)
  paint()
  emit(true)
  return true
}

/** Tests only: forget everything this module holds. */
export function resetAppThemeStoreForTests(): void {
  scopes = 0
  fontClass = undefined
  saved = null
  brand = null
  held = null
  lookOf = null
  unanswered = false
  preview = null
  standing = null
  snapshot = SERVER_APP_THEME_SNAPSHOT
  listeners.clear()
  savesInFlight = 0
  epoch = 0
}
